import React, { useEffect, useState } from 'react';
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Textarea,
    Input
} from "@heroui/react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession, useUser } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { Plus, Calendar } from "@phosphor-icons/react";

interface QuickNotesModalProps {
    isOpen: boolean;
    onClose: () => void;
    ticketId?: number;
    insuranceIssueId?: number;
    onNoteAdded?: () => void;
}

export const QuickNotesModal: React.FC<QuickNotesModalProps> = ({
    isOpen,
    onClose,
    ticketId,
    insuranceIssueId,
    onNoteAdded
}) => {
    const { session } = useSession();
    const { user } = useUser();
    const [message, setMessage] = useState('');
    const [remindMeAt, setRemindMeAt] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [existingNotes, setExistingNotes] = useState<any[]>([]);

    useEffect(() => {
        const fetchNotes = async () => {
            try {
                const url = ticketId
                    ? `${API_URL}/helpdesk/ticket/${ticketId}/action-notes`
                    : `${API_URL}/insurance/file/${insuranceIssueId}/action-notes`;
                const resp = await fetchWithAuth(url, { method: 'GET' }, session);
                if (resp.ok) {
                    const notes = await resp.json();
                    setExistingNotes(notes);
                } else {
                    setExistingNotes([]);
                }
            } catch {
                setExistingNotes([]);
            }
        };
        if (isOpen && (ticketId || insuranceIssueId)) {
            fetchNotes();
        }
    }, [isOpen, ticketId, insuranceIssueId, session]);

    const handleSubmit = async () => {
        if (!message.trim()) {
            toast.error('Voer een bericht in');
            return;
        }

        setIsSubmitting(true);
        try {
            const noteData = {
                message: message.trim(),
                remindMeAt: remindMeAt ? new Date(remindMeAt).toISOString() : null,
                userId: user?.id
            };

            const url = ticketId
                ? `${API_URL}/helpdesk/ticket/${ticketId}/action-notes`
                : `${API_URL}/insurance/file/${insuranceIssueId}/action-notes`;
            const response = await fetchWithAuth(
                url,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(noteData),
                },
                session
            );

            if (response.ok) {
                toast.success('Actie notitie toegevoegd');
                setMessage('');
                setRemindMeAt('');
                // Soft update notes list
                try {
                    const urlFetch = ticketId
                        ? `${API_URL}/helpdesk/ticket/${ticketId}/action-notes`
                        : `${API_URL}/insurance/file/${insuranceIssueId}/action-notes`;
                    const resp2 = await fetchWithAuth(urlFetch, { method: 'GET' }, session);
                    if (resp2.ok) {
                        const notes = await resp2.json();
                        setExistingNotes(notes);
                    }
                } catch {}
                onNoteAdded?.();
                // keep modal open so the user sees the new note immediately
            } else {
                throw new Error("Failed to add action note");
            }
        } catch (error) {
            console.error("Error adding action note:", error);
            toast.error("Error bij toevoegen van actie notitie");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setMessage('');
        setRemindMeAt('');
        onClose();
    };

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={handleClose}
            size="lg"
            classNames={{
                body: "py-6"
            }}
        >
            <ModalContent>
                <ModalHeader className="flex items-center gap-2">
                    <Plus className="h-5 w-5 text-moss-primary" />
                    <span>Snelle actie notitie</span>
                </ModalHeader>
                <ModalBody>
                    <div className="space-y-4">
                        {/* Existing notes */}
                        {existingNotes.length > 0 && (
                            <div className="max-h-48 overflow-y-auto border border-neutral-primary/10 rounded-md p-2">
                                <div className="text-xs font-medium text-neutral-secondary-mid mb-1">Eerdere notities</div>
                                <ul className="space-y-3">
                                    {existingNotes.map((n) => (
                                        <li key={n.id} className="text-sm text-neutral-primary flex items-start gap-2">
                                            {/* User avatar (Clerk) */}
                                            <img
                                                src={`https://api.clerk.com/v1/users/${n.userId}/image`}
                                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                                className="h-7 w-7 rounded-full object-cover"
                                                alt="gebruiker"
                                            />
                                            <div>
                                                <div className="font-medium">{n.message}</div>
                                                <div className="text-xs text-neutral-secondary-mid">
                                                    {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {/* Message Input */}
                        <div>
                            <label className="block text-sm font-medium text-neutral-primary mb-2">
                                Actie / Notitie
                            </label>
                            <Textarea
                                placeholder="Beschrijf de actie die je hebt ondernomen of een belangrijke notitie..."
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                minRows={3}
                                maxRows={6}
                                classNames={{
                                    input: "text-neutral-primary",
                                    inputWrapper: "border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary"
                                }}
                            />
                        </div>

                        {/* Reminder Date Input */}
                        <div>
                            <label className="block text-sm font-medium text-neutral-primary mb-2">
                                Herinnering (optioneel)
                            </label>
                            <Input
                                type="datetime-local"
                                value={remindMeAt}
                                onChange={(e) => setRemindMeAt(e.target.value)}
                                startContent={<Calendar className="h-4 w-4 text-neutral-secondary-mid" />}
                                classNames={{
                                    input: "text-neutral-primary",
                                    inputWrapper: "border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary"
                                }}
                                description="Stel een datum en tijd in om jezelf te herinneren aan deze actie"
                            />
                        </div>

                        {/* User Info */}
                        <div className="text-xs text-neutral-secondary-mid">
                            Toegevoegd door: {user?.firstName} {user?.lastName}
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button variant="light" onPress={handleClose}>
                        Annuleren
                    </Button>
                    <Button 
                        color="primary"
                        onPress={handleSubmit}
                        isLoading={isSubmitting}
                        isDisabled={!message.trim()}
                        className="bg-moss-primary text-white"
                    >
                        Notitie toevoegen
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}; 